import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Badge, BoutonSombre } from "./ui";

/* Les actions longues lancées par l'école — fabriquer les PDF, envoyer les factures,
   envoyer les rappels — ne doivent pas être dérangées. La petite pastille « Envoi de la
   facture en cours… » laissait toute la console cliquable : on pouvait modifier une
   facture pendant qu'elle partait, lancer une deuxième action par-dessus la première, ou
   fermer l'onglet au milieu d'une génération faite paquet par paquet (demande de l'école,
   09.10.2026).

   Pendant le travail, cette fenêtre grise toute la console (plus rien n'est cliquable ni
   atteignable au clavier), montre l'avancement et fait demander confirmation au navigateur
   si l'on ferme ou recharge la page. À la fin, elle devient le bilan : ce qui est parti, ce
   qui ne l'est pas et pourquoi — rien ne disparaît sans avoir été lu.

   Utilisation :
     const travail = useProgression();
     travail.demarrer({ titre, total, faits, unite, attente, detail });
     travail.avancer({ faits, detail });
     await travail.conclure({ titre, ton, texte, reussis, erreurs, ecartees, points });
     travail.arreter();          // fin sans bilan (action courte)
     … et {travail.fenetre} dans le rendu. */

export const CONSIGNE_ATTENTE =
  "Ne ferme pas cette page et ne touche à rien : la console te dit quand c'est fini.";

const TONS = {
  succes: { Icon: CheckCircle2, couleur: "#15803D", fond: "#F0FDF4", bord: "#BBF7D0" },
  attention: { Icon: AlertTriangle, couleur: "#B45309", fond: "#FFFBEB", bord: "#FDE68A" },
  danger: { Icon: XCircle, couleur: "#B91C1C", fond: "#FEF2F2", bord: "#FECACA" },
};

/* [singulier, pluriel] : « 1 / 1 envoyée », « 3 / 8 envoyées ». */
const accord = (n, mots) => (Array.isArray(mots) ? mots[n > 1 ? 1 : 0] : mots || "");

const duree = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")}`;
};

/* Une coupure réseau arrive du navigateur en anglais (« Failed to fetch », « Load
   failed ») : on la dit en clair dans le bilan. */
export const raisonErreur = (e) => {
  const m = String(e?.message || e || "");
  return /failed to fetch|networkerror|load failed|network request failed/i.test(m)
    ? "La connexion avec le serveur a été coupée" : m;
};

/* Trois noms puis « et N autre(s) » : la fenêtre reste lisible pour 80 familles. */
export const quelquesNoms = (noms, max = 3) => {
  const liste = [...new Set((noms || []).filter(Boolean))];
  return liste.length <= max ? liste.join(", ")
    : `${liste.slice(0, max).join(", ")} et ${liste.length - max} autre(s)`;
};

export function useProgression() {
  const [etat, setEtat] = useState(null);
  const suite = useRef(null);

  const demarrer = useCallback((options) => {
    setEtat({ faits: null, ...options, phase: "en_cours", debut: Date.now() });
  }, []);

  const avancer = useCallback((maj) => {
    setEtat((e) => (e && e.phase === "en_cours" ? { ...e, ...maj } : e));
  }, []);

  // Rend une promesse tenue quand l'école ferme le bilan : la suite (rechargement de la
  // liste, changement de vue) se fait après, comme avec l'ancien message de fin.
  const conclure = useCallback((bilan) => new Promise((resoudre) => {
    suite.current = resoudre;
    setEtat((e) => ({ ...bilan, phase: "fini", debut: e?.debut || Date.now(), fin: Date.now() }));
  }), []);

  const arreter = useCallback(() => setEtat(null), []);

  const fermer = useCallback(() => {
    setEtat(null);
    const resoudre = suite.current;
    suite.current = null;
    if (resoudre) resoudre();
  }, []);

  return {
    actif: !!etat, demarrer, avancer, conclure, arreter,
    fenetre: etat ? <FenetreProgression etat={etat} onFermer={fermer} /> : null,
  };
}

function ListeMotifs({ items, ton, testid }) {
  const g = { rouge: TONS.danger, ambre: TONS.attention }[ton];
  return (
    <ul className="rounded-xl px-4 py-3 mt-3 space-y-1.5 text-sm max-h-60 overflow-auto"
      style={{ background: g.fond, border: `1px solid ${g.bord}` }} data-testid={testid}>
      {items.map((x, k) => (
        <li key={k}>
          <strong style={{ color: g.couleur }}>{x.nom}</strong>
          {x.motif ? <span className="text-[var(--text)]"> — {x.motif}</span> : null}
        </li>
      ))}
    </ul>
  );
}

export function FenetreProgression({ etat, onFermer }) {
  const boite = useRef(null);
  const fini = etat.phase === "fini";
  const [maintenant, setMaintenant] = useState(() => Date.now());

  useEffect(() => {
    if (fini) return undefined;
    const tic = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(tic);
  }, [fini]);

  // Fermer ou recharger l'onglet pendant le travail : le navigateur demande d'abord.
  useEffect(() => {
    if (fini) return undefined;
    const retenir = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", retenir);
    return () => window.removeEventListener("beforeunload", retenir);
  }, [fini]);

  // Toute la console devient inerte (souris, clavier, lecteur d'écran) ; à la fermeture,
  // le curseur revient là où il était.
  useEffect(() => {
    const avant = document.activeElement;
    const racine = document.getElementById("root");
    if (racine) racine.inert = true;
    return () => {
      if (racine) racine.inert = false;
      if (avant && typeof avant.focus === "function" && document.contains(avant)) {
        avant.focus({ preventScroll: true });
      }
    };
  }, []);

  // Le clavier reste dans la fenêtre ; Échap ne fait rien pendant le travail et ferme le
  // bilan. Les raccourcis de la console (Échap de l'aide…) ne reçoivent plus rien.
  useEffect(() => {
    const clavier = (e) => {
      e.stopImmediatePropagation();
      if (e.key === "Escape") {
        e.preventDefault();
        if (fini) onFermer();
        return;
      }
      if (e.key !== "Tab" || !boite.current) return;
      const elements = [...boite.current.querySelectorAll("button:not([disabled]), summary")];
      if (!elements.length) { e.preventDefault(); boite.current.focus(); return; }
      const premier = elements[0], dernier = elements[elements.length - 1];
      if (!boite.current.contains(document.activeElement)) { e.preventDefault(); premier.focus(); }
      else if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
      else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
    };
    window.addEventListener("keydown", clavier, true);
    return () => window.removeEventListener("keydown", clavier, true);
  }, [fini, onFermer]);

  useEffect(() => {
    const cible = fini ? boite.current?.querySelector("[data-testid=progression-fermer]") : boite.current;
    cible?.focus({ preventScroll: true });
  }, [fini]);

  const mesure = !fini && typeof etat.faits === "number" && etat.total > 0;
  const pourcent = mesure ? Math.min(100, Math.round((etat.faits / etat.total) * 100)) : 0;
  const ton = TONS[etat.ton] || TONS.succes;
  const reussis = etat.reussis || null;
  const nbReussis = reussis ? (reussis.nb ?? (reussis.noms || []).length) : 0;
  const erreurs = etat.erreurs?.items || [];
  const ecartees = etat.ecartees?.items || [];

  return createPortal(
    <div className="progression-voile fixed inset-0 z-[90] flex px-4 py-8 overflow-auto"
      style={{ background: "rgba(15,27,45,0.62)" }} data-testid="progression">
      <div ref={boite} tabIndex={-1} role={fini ? "alertdialog" : "dialog"} aria-modal="true"
        aria-labelledby="progression-titre" aria-busy={!fini}
        className="bg-white rounded-2xl p-6 w-full m-auto outline-none"
        style={{ maxWidth: 520, boxShadow: "var(--shadow-lg)" }}
        data-testid={fini ? "progression-bilan" : "progression-en-cours"}>
        {!fini ? (
          <>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "var(--primary-tint)", color: "#0E7490" }}>
                <Loader2 size={20} className="animate-spin" />
              </span>
              <div className="min-w-0 pt-0.5">
                <h2 id="progression-titre" className="font-display text-xl tracking-tight">{etat.titre}</h2>
                {etat.detail && (
                  <p className="text-sm text-[var(--muted)] mt-0.5 break-words" data-testid="progression-detail">
                    {etat.detail}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5">
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }}
                role="progressbar" aria-label={etat.titre} aria-valuemin={0}
                aria-valuemax={mesure ? etat.total : undefined} aria-valuenow={mesure ? etat.faits : undefined}
                data-testid="progression-barre">
                {mesure ? (
                  <div className="h-full rounded-full relative overflow-hidden"
                    style={{ width: `${Math.max(pourcent, 3)}%`, background: "var(--primary)", transition: "width .6s ease" }}>
                    {/* Un reflet qui défile : la barre vit même entre deux paquets. */}
                    <div className="absolute inset-y-0 left-0 w-1/3 barre-avancement"
                      style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent)" }} />
                  </div>
                ) : (
                  <div className="h-full w-1/3 rounded-full barre-avancement"
                    style={{ background: "linear-gradient(90deg, transparent, var(--primary), transparent)" }} />
                )}
              </div>
              <div className="flex items-baseline justify-between gap-3 mt-2.5">
                <span className="text-sm font-semibold tabular-nums" aria-live="polite" data-testid="progression-compte">
                  {mesure
                    ? `${etat.faits} / ${etat.total} ${accord(etat.total, etat.unite || ["traitée", "traitées"])}`
                    : etat.attente || "Traitement en cours…"}
                </span>
                <span className="text-xs text-[var(--muted)] tabular-nums shrink-0" data-testid="progression-duree">
                  depuis {duree(maintenant - etat.debut)}
                </span>
              </div>
            </div>

            <div className="rounded-xl px-4 py-3 mt-5 flex gap-3 items-start text-sm"
              style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}
              data-testid="progression-consigne">
              <AlertTriangle size={17} className="shrink-0 mt-0.5" style={{ color: "#B45309" }} />
              <span>{CONSIGNE_ATTENTE}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                style={{ background: ton.fond, color: ton.couleur, border: `1px solid ${ton.bord}` }}>
                <ton.Icon size={20} />
              </span>
              <div className="min-w-0 pt-0.5">
                <h2 id="progression-titre" className="font-display text-xl tracking-tight">{etat.titre}</h2>
                {etat.texte && (
                  <p className="text-sm text-[var(--muted)] mt-1 whitespace-pre-line" data-testid="progression-texte">
                    {etat.texte}
                  </p>
                )}
              </div>
            </div>

            {(nbReussis > 0 || erreurs.length > 0 || ecartees.length > 0) && (
              <div className="flex flex-wrap gap-2 mt-5" data-testid="progression-comptes">
                {nbReussis > 0 && (
                  <Badge ton="vert" testid="progression-reussis">
                    ✓ {nbReussis} {accord(nbReussis, reussis.libelle)}
                  </Badge>
                )}
                {erreurs.length > 0 && (
                  <Badge ton="rouge" testid="progression-nb-erreurs">
                    ✗ {erreurs.length} {accord(erreurs.length, etat.erreurs.libelle || ["en erreur", "en erreur"])}
                  </Badge>
                )}
                {ecartees.length > 0 && (
                  <Badge ton="ambre" testid="progression-nb-ecartees">
                    – {ecartees.length} {accord(ecartees.length, etat.ecartees.libelle)}
                  </Badge>
                )}
              </div>
            )}

            {erreurs.length > 0 && <ListeMotifs items={erreurs} ton="rouge" testid="progression-erreurs" />}
            {ecartees.length > 0 && <ListeMotifs items={ecartees} ton="ambre" testid="progression-ecartees" />}

            {(etat.points || []).length > 0 && (
              <ul className="text-xs text-[var(--muted)] mt-3 space-y-1" data-testid="progression-points">
                {etat.points.map((p, k) => <li key={k}>· {p}</li>)}
              </ul>
            )}

            {reussis && (reussis.noms || []).length > 0 && (
              <details className="mt-3 text-xs text-[var(--muted)]" data-testid="progression-liste-reussis">
                <summary className="cursor-pointer select-none">
                  Voir {nbReussis > 1 ? `les ${nbReussis}` : "la"} famille{nbReussis > 1 ? "s" : ""}
                </summary>
                <p className="mt-1.5 leading-relaxed">{[...new Set(reussis.noms.filter(Boolean))].join(" · ")}</p>
              </details>
            )}

            <div className="flex items-center gap-3 mt-6">
              <span className="text-xs text-[var(--muted)]" data-testid="progression-duree-totale">
                Terminé en {duree((etat.fin || Date.now()) - etat.debut)}
              </span>
              <span className="ml-auto">
                <BoutonSombre onClick={onFermer} testid="progression-fermer">Fermer</BoutonSombre>
              </span>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
